import type { Price } from '../interfaces/Price';
import { SUPPORT_EMAIL_MAILTO } from '../constants/contactConstants';

/**
 * Pricing plans for all customers.
 * Free and Enterprise have no Stripe link (noStripe: true).
 * Add your Stripe payment link IDs to test_url/url when plans are created in Stripe.
 */
export const pricingPlansCall = [
  {
    id: 'call_free',
    title: 'Free',
    price: '€ 0',
    features: ['10 minutes', 'Knowledge base integration', 'Demo call'],
    test_url: '',
    url: '',
    minutes: 10,
    concurrentCalls: 1,
    noStripe: true,
    planType: 'call' as const,
    tier: 'free' as const,
  },
  {
    id: 'call_starter_t1',
    title: 'Starter',
    price: '€ 199.99 / month',
    features: [
      '500 minutes (~100 calls)',
      '2 simultaneous calls',
      'KB integration',
      'Calls transfer / schedule meetings',
      'Basic analytics',
    ],
    test_url: 'https://buy.stripe.com/test_14AeVc6ZT2U2672fB40co0c',
    url: 'https://buy.stripe.com/bJe14mbg9bqy0MIfB40co0e',
    minutes: 500,
    concurrentCalls: 2,
    planType: 'call' as const,
    tier: 'starter' as const,
  },
  {
    id: 'call_starter_t2',
    title: 'Starter',
    price: '€ 399.99 / month',
    features: [
      '1000 minutes (~200 calls)',
      '2 simultaneous calls',
      'KB integration',
      'Calls transfer / schedule meetings',
      'Basic analytics',
    ],
    test_url: 'https://buy.stripe.com/test_bJebJ05VPeCK8fabkO0co0d',
    url: 'https://buy.stripe.com/3cIfZgesleCK7b62Oi0co0f',
    minutes: 1000,
    concurrentCalls: 2,
    planType: 'call' as const,
    tier: 'starter' as const,
  },
  {
    id: 'call_business_t1',
    title: 'Business',
    price: '€ 499.99 / month',
    features: [
      '1500 minutes (~300 calls)',
      '5 simultaneous calls',
      'KB integration',
      'CRM integration',
      'Advanced analytics',
      '2h consultancy/month',
    ],
    test_url: 'https://buy.stripe.com/test_bJe14mbg9bqy0MIfB40co0e',
    url: 'https://buy.stripe.com/6oU7sK3NH52a9jegF80co0g',
    minutes: 1500,
    concurrentCalls: 5,
    planType: 'call' as const,
    tier: 'business' as const,
  },
  {
    id: 'call_business_t2',
    title: 'Business',
    price: '€ 799.99 / month',
    features: [
      '2500 minutes (~500 calls)',
      '5 simultaneous calls',
      'KB integration',
      'CRM integration',
      'Advanced analytics',
      '2h consultancy/month',
    ],
    test_url: 'https://buy.stripe.com/test_6oU7sK3NH52a9jegF80co0g',
    url: 'https://buy.stripe.com/5kQ14mbg90LU672dsW0co0h',
    minutes: 2500,
    concurrentCalls: 5,
    planType: 'call' as const,
    tier: 'business' as const,
  },
  {
    id: 'call_business_t3',
    title: 'Business',
    price: '€ 1399.99 / month',
    features: [
      '5000 minutes (~1000 calls)',
      '5 simultaneous calls',
      'KB integration',
      'CRM integration',
      'Advanced analytics',
      '2h consultancy/month',
    ],
    test_url: 'https://buy.stripe.com/test_3cIfZgesleCK7b62Oi0co0f',
    url: 'https://buy.stripe.com/5kQ8wO9817ai3YUgF80co0i',
    minutes: 5000,
    concurrentCalls: 5,
    planType: 'call' as const,
    tier: 'business' as const,
  },
  {
    id: 'call_enterprise',
    title: 'Enterprise',
    price: 'Custom',
    features: ['5000+ minutes', 'Custom limits', 'Dedicated support'],
    test_url: SUPPORT_EMAIL_MAILTO,
    url: SUPPORT_EMAIL_MAILTO,
    minutes: 0,
    concurrentCalls: 0,
    noStripe: true,
    planType: 'call' as const,
    tier: 'enterprise' as const,
  },
];

export const pricingPlansChat = [
  {
    id: 'chat_free',
    title: 'Free',
    price: '€ 0',
    features: ['Knowledge base integration', 'Demo link available'],
    test_url: '',
    url: '',
    minutes: 0,
    concurrentCalls: 0,
    messages: 0,
    noStripe: true,
    planType: 'chat' as const,
    tier: 'free' as const,
  },
  {
    id: 'chat_starter_t1',
    title: 'Starter',
    price: '€ 99.99 / month',
    features: [
      '600 messages (~200 conversations)',
      'KB integration',
      'Website integration',
    ],
    test_url: 'https://buy.stripe.com/test_4gM9AS5VP3Y6cvqagK0co0j',
    url: 'https://buy.stripe.com/dRm6oG4RL7ai52Y0Ga0co09',
    minutes: 0,
    concurrentCalls: 0,
    messages: 600,
    planType: 'chat' as const,
    tier: 'starter' as const,
  },
  {
    id: 'chat_starter_t2',
    title: 'Starter',
    price: '€ 199.99 / month',
    features: [
      '1200 messages (~300 conversations)',
      'KB integration',
      'Website integration',
    ],
    test_url: 'https://buy.stripe.com/test_bJe00i6ZTfGObrmbkO0co0k',
    url: 'https://buy.stripe.com/00w14mgAtdyGfHC1Ke0co0a',
    minutes: 0,
    concurrentCalls: 0,
    messages: 1200,
    planType: 'chat' as const,
    tier: 'starter' as const,
  },
  {
    id: 'chat_business_t1',
    title: 'Business',
    price: '€ 249.99 / month',
    features: [
      '2000 messages (~500 conversations)',
      'KB integration',
      'Website integration',
      'CRM integration',
    ],
    test_url: 'https://buy.stripe.com/test_8x27sK2JD9iqfHCagK0co0l',
    url: 'https://buy.stripe.com/aFa8wO0Bv9iq9jeex00co0b',
    minutes: 0,
    concurrentCalls: 0,
    messages: 2000,
    planType: 'chat' as const,
    tier: 'business' as const,
  },
  {
    id: 'chat_business_t2',
    title: 'Business',
    price: '€ 349.99 / month',
    features: [
      '3000 messages (~750 conversations)',
      'KB integration',
      'Website integration',
      'CRM integration',
    ],
    test_url: 'https://buy.stripe.com/test_7sYfZgbg9amu9je0Ga0co0m',
    url: 'https://buy.stripe.com/14AeVc6ZT2U2672fB40co0c',
    minutes: 0,
    concurrentCalls: 0,
    messages: 3000,
    planType: 'chat' as const,
    tier: 'business' as const,
  },
  {
    id: 'chat_business_t3',
    title: 'Business',
    price: '€ 449.99 / month',
    features: [
      '4000 messages (~1000 conversations)',
      'KB integration',
      'Website integration',
      'CRM integration',
    ],
    test_url: 'https://buy.stripe.com/test_bJe7sKac53Y62UQ9cG0co0n',
    url: 'https://buy.stripe.com/bJebJ05VPeCK8fabkO0co0d',
    minutes: 0,
    concurrentCalls: 0,
    messages: 4000,
    planType: 'chat' as const,
    tier: 'business' as const,
  },
  {
    id: 'chat_enterprise',
    title: 'Enterprise',
    price: 'Custom',
    features: ['4000+ messages', 'Custom limits', 'Dedicated support'],
    test_url: SUPPORT_EMAIL_MAILTO,
    url: SUPPORT_EMAIL_MAILTO,
    minutes: 0,
    concurrentCalls: 0,
    messages: 0,
    noStripe: true,
    planType: 'chat' as const,
    tier: 'enterprise' as const,
  },
];

/**
 * Bundle plans V2 — one monthly allowance: call minutes or chat messages (shared pool)
 *
 * TODO: Add Stripe payment links when products/prices are created:
 * 1. Create products in Stripe Dashboard for each bundle tier
 * 2. Create prices (€129.99, €269.99, €329.99, €519.99, €829.99)
 * 3. Generate payment links for each price
 * 4. Add test_url (test mode) and url (live mode) below
 * 5. Add Stripe price IDs to backend global_variables.py:
 *    - SBX_STRIPE_PRICE_PLAN_V2_MAPPING (test price IDs)
 *    - PRD_STRIPE_PRICE_PLAN_V2_MAPPING (live price IDs)
 */
export const pricingPlansBundle = [
  {
    id: 'bundle_starter_t1',
    title: 'Starter',
    price: '€ 129.99 / month',
    features: [
      '350 minutes (~70 calls) or 900 messages (~450 conversations)',
      'Features: same as starter plan for both products',
    ],
    test_url: 'https://buy.stripe.com/test_00w14m6ZT52aeDyex00co0o',
    url: 'https://buy.stripe.com/bJe00i6ZTfGObrmbkO0co0k',
    minutes: 350,
    concurrentCalls: 2,
    messages: 900,
    planType: 'bundle' as const,
    tier: 'starter' as const,
  },
  {
    id: 'bundle_starter_t2',
    title: 'Starter',
    price: '€ 269.99 / month',
    features: [
      '750 minutes (~150 calls) or 1800 messages (~900 conversations)',
      'Features: same as starter plan for both products',
    ],
    test_url: 'https://buy.stripe.com/test_fZucN45VP7ai0MIbkO0co0q',
    url: 'https://buy.stripe.com/8x27sK2JD9iqfHCagK0co0l',
    minutes: 750,
    concurrentCalls: 2,
    messages: 1800,
    planType: 'bundle' as const,
    tier: 'starter' as const,
  },
  {
    id: 'bundle_business_t1',
    title: 'Business',
    price: '€ 329.99 / month',
    features: [
      '1100 minutes (~220 calls) or 3000 messages (~750 conversations)',
      'Features: same as business plan for both products',
    ],
    test_url: '', // TODO: Add Stripe test payment link
    url: 'https://buy.stripe.com/7sYfZgbg9amu9je0Ga0co0m',
    minutes: 1100,
    concurrentCalls: 5,
    messages: 3000,
    planType: 'bundle' as const,
    tier: 'business' as const,
  },
  {
    id: 'bundle_business_t2',
    title: 'Business',
    price: '€ 519.99 / month',
    features: [
      '1750 minutes (~350 calls) or 4600 messages (~1150 conversations)',
      'Features: same as business plan for both products',
    ],
    test_url: 'https://buy.stripe.com/test_7sY4gyac552a6729cG0co0s',
    url: 'https://buy.stripe.com/bJe7sKac53Y62UQ9cG0co0n',
    minutes: 1750,
    concurrentCalls: 5,
    messages: 4600,
    planType: 'bundle' as const,
    tier: 'business' as const,
  },
  {
    id: 'bundle_business_t3',
    title: 'Business',
    price: '€ 829.99 / month',
    features: [
      '3300 minutes (~660 calls) or 8200 messages (~2050 conversations)',
      'Features: same as business plan for both products',
    ],
    test_url: 'https://buy.stripe.com/test_aFaeVcbg966e3YUfB40co0r',
    url: 'https://buy.stripe.com/00w14m6ZT52aeDyex00co0o',
    minutes: 3300,
    concurrentCalls: 5,
    messages: 8200,
    planType: 'bundle' as const,
    tier: 'business' as const,
  },
  {
    id: 'bundle_enterprise',
    title: 'Enterprise',
    price: 'Custom',
    features: ['Custom limits', 'Dedicated support'],
    test_url: SUPPORT_EMAIL_MAILTO,
    url: SUPPORT_EMAIL_MAILTO,
    minutes: 0,
    concurrentCalls: 0,
    messages: 0,
    noStripe: true,
    planType: 'bundle' as const,
    tier: 'enterprise' as const,
  },
];

/** One-time consultation package (help / upsell), not a subscription tier. */
export const consultationPackagePlan: Price = {
  id: 'consultation',
  title: '2 Hours Consultation',
  price: '€ 300',
  features: [
    'Get assistance, we will reach out to you after this one time purchase.',
  ],
  test_url: 'https://buy.stripe.com/test_aFa8wO0Bv9iq9jeex00co0b',
  url: 'https://buy.stripe.com/5kQaEWdoh3Y6eDygF80co08',
  minutes: 0,
  concurrentCalls: 0,
};
